<template>
  <div>
    <template v-if="ready">
      <div v-if="hasApproved" class="result-wrap">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">
            {{ $t("risk.last_completed_result") || $t("unit.last_completed_result") }}
          </h5>
          <small class="text-muted">
            {{ $t("risk.reference_number") || $t("unit.reference_number") }}:
            {{ referenceNumber }} · {{ formatDate(evaluationDate) }}
          </small>
        </div>

        <risk-answers-view
          :snapshot="snapshot"
          :answers="answersRaw"
          :alarms-only="alarmsOnly"
          :collapsible="true"
          :start-collapsed="false"
          :show-comments="showComments"
        />
      </div>

      <div v-else class="text-muted">
        {{ $t('risk.no_completed_evaluations') || $t('unit.no_completed_evaluations') }}
      </div>
    </template>

    <div v-else class="text-center">
      <b-spinner :label="$t('actions.loading')" variant="primary" />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "@vue/composition-api";
import axios from "@/libs/axios";
import { BSpinner } from "bootstrap-vue";
import RiskAnswersView from "@/components/BuildRiskForm/RiskAnswersView.vue";

export default {
  name: "RiskAssessmentStep",
  components: { BSpinner, RiskAnswersView },
  props: {
    // we need at least{ id, id_tenant }
    propItemData: { type: Object, required: true },
    //Optionals to reuse in view
    alarmsOnly: { type: Boolean, default: false },
    showComments: { type: Boolean, default: true },
  },
  setup(props) {
    const ready = ref(false);

    //approved evaluation
    const evaluationId = ref(null);
    const referenceNumber = ref("");
    const evaluationDate = ref(null);

    // data for the view
    const snapshot = ref([]);    // template_snapshot (array of fields)
    const answersRaw = ref({});  // data raw (object/array)

    const unitId = computed(() => props.propItemData?.id);

    //Helpers
    const safeJson = (v, fallback) => {
      if (v == null) return fallback;
      if (typeof v === "string") {
        try { return JSON.parse(v); } catch { return fallback; }
      }
      if (typeof v === "object") return v;
      return fallback;
    };

    const normalizeStatus = s => String(s || "").toLowerCase();

    const pickLatest = (list) =>
      (list || []).reduce((acc, ev) => {
        const t = new Date(ev.updated_at || ev.created_at || ev.evaluation_date || 0).getTime();
        if (!acc || t > acc._t) return { ...ev, _t: t };
        return acc;
      }, null);

    const fetchLastApprovedEvaluation = async (idUnit) => {
      const { data } = await axios.get("/v1/risk_evaluations", {
        params: { id_unit: idUnit, with: ["last_status"] },
      });
      const payload = data?.data;
      const list = Array.isArray(payload) ? payload : (payload ? [payload] : []);
      const approved = list.filter(ev => normalizeStatus(ev?.last_status?.name) === "approved");
      return pickLatest(approved) || null;
    };

    const fetchFormAnswer = async (evId) => {
      const { data } = await axios.get(`/v1/risk_form_answers/get-form-by-evaluation/${evId}`);
      const fa = data?.data || data || {};
      snapshot.value = safeJson(fa.template_snapshot, []);
      answersRaw.value = safeJson(fa.data, {});
    };

    const hasApproved = computed(() => !!evaluationId.value);

    const formatDate = (iso) => {
      if (!iso) return "";
      try { return new Date(iso).toLocaleString(); } catch { return iso; }
    };

    const load = async () => {
      if (!unitId.value) return;
      ready.value = false;
      evaluationId.value = null;
      snapshot.value = [];
      answersRaw.value = {};

      try {
        const latest = await fetchLastApprovedEvaluation(unitId.value);
        if (latest?.id) {
          evaluationId.value = latest.id;
          referenceNumber.value = latest.reference_number || latest.reference || latest.id;
          evaluationDate.value = latest.evaluation_date || latest.updated_at || latest.created_at;
          await fetchFormAnswer(latest.id);
        }
      } catch (e) {
        // if there is no approved we show the empty state
      } finally {
        ready.value = true;
      }
    };

    onMounted(load);
    // if the unit change
    watch(unitId, (nv, ov) => { if (nv && nv !== ov) load(); });

    return {
      ready,
      hasApproved,
      referenceNumber,
      evaluationDate,
      snapshot,
      answersRaw,
      formatDate,
    };
  },
};
</script>

<style scoped>
.result-wrap { position: relative; z-index: 1; }
</style>
